import { CustomFile } from '../Types/file';

/**
 * Removes leading and trailing slashes from a given path string.
 * @param path - The file or folder path to normalize.
 * @returns A cleaned path without leading or trailing slashes.
 */
const normalizePath = (path?: string): string => path?.replace(/^\/+|\/+$/g, '') ?? '';

/**
 * Extracts all unique folder paths from the relative paths of the provided files.
 * Supports nested folder structure (e.g., /A/B/C produces A, A/B, A/B/C).
 *
 * @param files - List of CustomFile objects to extract folder paths from.
 * @returns Sorted array of unique folder paths.
 */
export const extractUniqueFolders = (files: CustomFile[]): string[] => {
  const folderSet = new Set<string>();

  for (const file of files) {
    const normalizedPath = normalizePath(file.relativePath || '');
    if (!normalizedPath) continue;

    const parts = normalizedPath.split('/');

    // Add each hierarchical level: "A", "A/B", "A/B/C"
    for (let i = 0; i < parts.length; i++) {
      const subPath = parts.slice(0, i + 1).join('/');
      folderSet.add(subPath);
    }
  }

  return Array.from(folderSet).sort((a, b) => a.localeCompare(b));
};

/**
 * Filters and returns files that are direct children of the specified folder.
 * Assumes a match when the file's relativePath equals the folder path after normalization.
 *
 * @param folderPath - The folder path to match files against.
 * @param files - List of all available files to filter.
 * @returns Array of CustomFile objects that belong to the specified folder.
 */
export const getFilesInFolder = (folderPath: string, files: CustomFile[]): CustomFile[] => {
  const normalizedFolder = normalizePath(folderPath);

  return files.filter((file) => normalizePath(file.relativePath || '') === normalizedFolder);
};

function hasLastModified(file: CustomFile): file is CustomFile & { lastModified: string } {
  return typeof file.lastModified === 'string';
}

/**
 * Extracts the latest metadata (lastModified and userId) from files within a given folder path.
 *
 * This function searches for all files whose `relativePath` starts with the given `folderPath`.
 * It then determines which file has the most recent `lastModified` date and returns its metadata.
 *
 * If no files are found within the folder, it returns `null`.
 * If none of the files have a `lastModified` value, the first file in the folder is used as fallback.
 *
 * @param folderPath - The folder path to match against each file's `relativePath`.
 * @param files - The full list of files to search.
 * @returns An object containing `lastModified` and `userId`, or `null` if no files are found.
 */
export const getFolderMetadata = (
  folderPath: string,
  files: CustomFile[]
): { lastModified?: string; userId?: string } | null => {
  const folderFiles = files.filter((file) => {
    if (!file.relativePath) return false;
    const trimmed = file.relativePath.replace(/^\/+|\/+$/g, '');
    return trimmed.startsWith(folderPath);
  });

  if (!folderFiles.length) return null;

  const filesWithModified = folderFiles.filter(hasLastModified);

  const sorted = filesWithModified.sort(
    (a, b) => new Date(b.lastModified).getTime() - new Date(a.lastModified).getTime()
  );

  const latest = sorted[0] ?? folderFiles[0];

  return {
    lastModified: latest.lastModified,
    userId: latest.userId,
  };
};

export const getFileExtension = (fileName: string): string => {
  return fileName.split('.').pop() || '';
};
